import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import {
  catalogKindLabel,
  type CatalogSearchHit,
} from '@/components/CatalogManageModal/catalogSearchTypes';
import './CatalogUnifiedSearch.css';

const MIN_SEARCH = 2;
const SEARCH_LIMIT = 8;
const SEARCH_DEBOUNCE_MS = 300;

export interface CatalogUnifiedSearchProps {
  disabled?: boolean;
  debounceFlushKey?: string | number;
  /** Начальная строка поиска (при смене flushKey) */
  initialQuery?: string;
  onSelect: (hit: CatalogSearchHit) => void;
}

export function CatalogUnifiedSearch({
  disabled = false,
  debounceFlushKey,
  initialQuery = '',
  onSelect,
}: CatalogUnifiedSearchProps) {
  const reactId = useId().replace(/:/g, '');
  const listDomId = `catalog-unified-search-${reactId}-list`;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement | null>(null);

  const [inputValue, setInputValue] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  useEffect(() => {
    setInputValue(initialQuery);
    if (initialQuery.trim().length >= MIN_SEARCH) {
      setOpen(true);
    }
  }, [debounceFlushKey, initialQuery]);

  const debouncedSearch = useDebouncedValue(inputValue.trim(), SEARCH_DEBOUNCE_MS, debounceFlushKey);
  const queryEnabled = !disabled && debouncedSearch.length >= MIN_SEARCH;

  const searchQuery = useQuery({
    queryKey: ['catalogManageSearch', debouncedSearch],
    queryFn: async (): Promise<CatalogSearchHit[]> => {
      const q = debouncedSearch;
      const [personsRes, studiosRes, legalRes] = await Promise.all([
        api.persons.list(q, { limit: SEARCH_LIMIT, offset: 0 }),
        api.studios.list(q, { limit: SEARCH_LIMIT, offset: 0 }),
        api.legalEntities.list(q, { limit: SEARCH_LIMIT, offset: 0 }),
      ]);
      const persons: CatalogSearchHit[] = personsRes.items.map((p) => ({
        kind: 'person',
        id: p.id,
        label: p.name?.trim() || `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || 'Без имени',
      }));
      const studios: CatalogSearchHit[] = studiosRes.items.map((s) => ({
        kind: 'studio',
        id: s.id,
        label: s.name,
        subtitle: s.legal_entity?.name?.trim() || undefined,
      }));
      const legalEntities: CatalogSearchHit[] = legalRes.items.map((le) => ({
        kind: 'legal_entity',
        id: le.id,
        label: le.name,
      }));
      return [...persons, ...studios, ...legalEntities];
    },
    enabled: queryEnabled,
    staleTime: 15_000,
  });

  const hits = searchQuery.data ?? [];
  const showDropdown = open && queryEnabled;
  const dropdownStyle = useDropdownPosition(containerRef, showDropdown, {
    dropdownRef: dropdownWrapRef,
  });

  useEffect(() => {
    setHighlightIndex(0);
  }, [debouncedSearch, hits.length]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (dropdownWrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const handleSelect = useCallback(
    (hit: CatalogSearchHit) => {
      onSelect(hit);
      setInputValue('');
      setOpen(false);
      inputRef.current?.blur();
    },
    [onSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || searchQuery.isFetching || hits.length === 0) {
      if (e.key === 'Escape') {
        setOpen(false);
        setInputValue('');
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, hits.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const hit = hits[highlightIndex];
      if (hit) handleSelect(hit);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const dropdownBody = useMemo(() => {
    if (searchQuery.isFetching) {
      return (
        <div className="catalog-unified-search__dropdown-hint" role="status">
          Поиск…
        </div>
      );
    }
    if (hits.length === 0) {
      return (
        <div className="catalog-unified-search__dropdown-hint" role="status">
          Ничего не найдено
        </div>
      );
    }
    return (
      <OverlayScrollArea
        contentRef={listRef}
        element="ul"
        id={listDomId}
        className="catalog-unified-search__dropdown-list"
        role="listbox"
      >
        {hits.map((hit, i) => (
          <li
            key={`${hit.kind}:${hit.id}`}
            id={`${listDomId}-opt-${i}`}
            role="option"
            aria-selected={i === highlightIndex}
            className={`catalog-unified-search__dropdown-option${
              i === highlightIndex ? ' catalog-unified-search__dropdown-option_highlight' : ''
            }`}
            onMouseDown={(ev) => {
              ev.preventDefault();
              handleSelect(hit);
            }}
            onMouseEnter={() => setHighlightIndex(i)}
          >
            <span className="catalog-unified-search__option">
              <span className="catalog-unified-search__option-kind">
                <span className="catalog-unified-search__option-kind-badge">
                  {catalogKindLabel(hit.kind)}
                </span>
              </span>
              <span className="catalog-unified-search__option-main">
                <span className="catalog-unified-search__option-label">{hit.label}</span>
                {hit.kind === 'studio' && hit.subtitle ? (
                  <span className="catalog-unified-search__option-sub">{hit.subtitle}</span>
                ) : null}
              </span>
            </span>
          </li>
        ))}
      </OverlayScrollArea>
    );
  }, [searchQuery.isFetching, hits, listDomId, highlightIndex, handleSelect]);

  return (
    <div ref={containerRef} className="catalog-unified-search">
      <div
        className={`catalog-unified-search__field${inputValue ? ' catalog-unified-search__field_has-clear' : ''}`}
      >
        <input
          ref={inputRef}
          className="catalog-unified-search__input"
          type="text"
          value={inputValue}
          disabled={disabled}
          placeholder="Поиск персон, студий, юрлиц…"
          aria-label="Поиск по каталогу"
          aria-autocomplete="list"
          aria-controls={listDomId}
          aria-expanded={showDropdown}
          autoComplete="off"
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {inputValue && !disabled ? (
          <ComboClearButton
            aria-label="Очистить поиск"
            onClick={() => {
              setInputValue('');
              setOpen(false);
              inputRef.current?.focus();
            }}
          />
        ) : null}
      </div>
      {showDropdown
        ? createPortal(
            <div
              ref={dropdownWrapRef}
              className="catalog-unified-search__dropdown-wrap"
              style={dropdownStyle}
            >
              {dropdownBody}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
